import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../context/AuthContext";
import { Search, Loader2, UserPlus, Ship, Check } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const HeaderSearch = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [people, setPeople] = useState([]);
  const [comms, setComms] = useState([]);
  const [cos, setCos] = useState([]);
  const wrap = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const run = useCallback(async (query) => {
    const t = query.trim();
    if (t.length < 2) { setPeople([]); setComms([]); setCos([]); return; }
    setLoading(true);
    try {
      const ql = t.toLowerCase();
      const [cRes, coRes] = await Promise.all([api.get("/communities"), api.get("/companies")]);
      setComms(cRes.data.communities.filter((c) => c.name.toLowerCase().includes(ql)));
      setCos(coRes.data.companies.filter((c) => c.name.toLowerCase().includes(ql) || c.handle.includes(ql)));
      if (t.length >= 3) {
        setPeople((await api.get(`/friends/search?q=${encodeURIComponent(t)}`)).data.results);
      } else setPeople([]);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => run(q), 300);
    return () => clearTimeout(t);
  }, [q, run]);

  const addFriend = async (p) => {
    try {
      await api.post("/friends/request", { to_user_id: p.user_id });
      setPeople((l) => l.map((x) => (x.user_id === p.user_id ? { ...x, status: "requested" } : x)));
      toast({ title: "Friend request sent" });
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not send request", variant: "destructive" }); }
  };

  const toggleComm = async (c) => {
    try {
      await api.post(`/communities/${c.id}/${c.joined ? "leave" : "join"}`);
      setComms((l) => l.map((x) => (x.id === c.id ? { ...x, joined: !c.joined } : x)));
    } catch { toast({ title: "Action failed", variant: "destructive" }); }
  };

  const toggleCo = async (c) => {
    try {
      await api.post(`/companies/${c.id}/${c.following ? "unfollow" : "follow"}`);
      setCos((l) => l.map((x) => (x.id === c.id ? { ...x, following: !c.following } : x)));
    } catch { toast({ title: "Action failed", variant: "destructive" }); }
  };

  const empty = !loading && q.trim().length >= 2 && people.length === 0 && comms.length === 0 && cos.length === 0;

  return (
    <div ref={wrap} className="relative w-full">
      <form
        onSubmit={(e) => { e.preventDefault(); setOpen(true); run(q); }}
        className="flex h-11 w-full items-center rounded-full border border-transparent bg-gray-100 pl-5 pr-1 transition-colors duration-200 focus-within:border-slate-300 focus-within:bg-white focus-within:shadow-sm"
        role="search"
        data-testid="header-search-form"
      >
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search seafarers, communities or companies…"
          data-testid="header-search-input"
          className="min-w-0 flex-1 bg-transparent text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none"
        />
        <button
          type="submit"
          aria-label="Search"
          data-testid="header-search-button"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#FF4500] text-white transition-colors duration-200 hover:bg-[#e03d00]"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" strokeWidth={2.5} />}
        </button>
      </form>

      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-12 z-50 max-h-[70vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white p-2 shadow-lg" data-testid="header-search-dropdown">
          {empty && <p className="px-3 py-6 text-center text-sm text-gray-400" data-testid="header-search-empty">No matches for "{q}"</p>}

          {people.length > 0 && (
            <div>
              <div className="px-3 pb-1 pt-2 text-[11px] font-extrabold uppercase tracking-wide text-gray-400">Seafarers</div>
              {people.map((p) => (
                <div key={p.user_id} className="flex items-center gap-2 rounded-xl px-3 py-2 hover:bg-gray-50" data-testid={`hs-person-${p.user_id}`}>
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-800 text-xs font-black text-white">{p.name.charAt(0)}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold text-gray-800">{p.name}</div>
                    <div className="truncate text-xs text-gray-400 capitalize">{p.rank || p.role}</div>
                  </div>
                  {p.status === "friends" ? <span className="text-xs font-bold text-[#002855]">Friends</span>
                    : p.status === "requested" ? <span className="text-xs font-semibold text-gray-400">Requested</span>
                    : <button onClick={() => addFriend(p)} data-testid={`hs-add-${p.user_id}`} className="flex h-7 items-center gap-1 rounded-full bg-[#FF4500] px-3 text-xs font-bold text-white hover:bg-[#e03d00]"><UserPlus className="h-3 w-3" /> Add</button>}
                </div>
              ))}
            </div>
          )}

          {comms.length > 0 && (
            <div>
              <div className="px-3 pb-1 pt-2 text-[11px] font-extrabold uppercase tracking-wide text-gray-400">Communities</div>
              {comms.map((c) => (
                <div key={c.id} className="flex items-center gap-2 rounded-xl px-3 py-2 hover:bg-gray-50" data-testid={`hs-community-${c.id}`}>
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200 bg-slate-100 text-[10px] font-black text-slate-700">{c.name.charAt(0).toUpperCase()}</span>
                  <button onClick={() => { setOpen(false); navigate(`/c/${encodeURIComponent(c.name)}`); }} className="min-w-0 flex-1 text-left" data-testid={`hs-community-open-${c.id}`}>
                    <div className="truncate text-sm font-bold text-gray-800">{c.name}</div>
                    <div className="truncate text-xs text-gray-400">{c.member_count.toLocaleString()} members</div>
                  </button>
                  <button onClick={() => toggleComm(c)} data-testid={`hs-comm-toggle-${c.id}`}
                    className={`flex h-7 items-center gap-1 rounded-full px-3 text-xs font-bold ${c.joined ? "border border-slate-200 bg-slate-50 text-slate-600" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
                    {c.joined ? <><Check className="h-3 w-3" /> Joined</> : "Follow"}
                  </button>
                </div>
              ))}
            </div>
          )}

          {cos.length > 0 && (
            <div>
              <div className="px-3 pb-1 pt-2 text-[11px] font-extrabold uppercase tracking-wide text-gray-400">Companies</div>
              {cos.map((c) => (
                <div key={c.id} className="flex items-center gap-2 rounded-xl px-3 py-2 hover:bg-gray-50" data-testid={`hs-company-${c.id}`}>
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#002855] text-white"><Ship className="h-4 w-4" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold text-gray-800">{c.name}</div>
                    <div className="truncate text-xs text-gray-400">{c.fleet}</div>
                  </div>
                  <button onClick={() => toggleCo(c)} data-testid={`hs-co-toggle-${c.id}`}
                    className={`flex h-7 items-center gap-1 rounded-full px-3 text-xs font-bold ${c.following ? "border border-slate-200 bg-slate-50 text-slate-600" : "bg-[#002855] text-white hover:bg-[#001d40]"}`}>
                    {c.following ? <><Check className="h-3 w-3" /> Following</> : "Follow"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderSearch;
